// components
import Chat from '@/ui/pages/Chat/Chat'
import Assistant from '@/utils/assistant/assistant'

// utils
import utils from './utils'
import { AssistantTypes, RenderOptions } from '../constants'

import { Config } from '@/shared/types/config'
import { TFunction } from 'i18next'
import { SpaceConfig, AssistantConfig, Space, ParseAssistantResult, ParseSpaceResult } from '@/shared/types/space'
import { NonIndexRouteObject, RouteObject } from 'react-router-dom'

/**
 * Represents a collection of assistants.
 */
class SpaceParser {
    #routes: RouteObject[] = []
    #resources: Space[] = []
    activeAssistantId = undefined
    translate: TFunction | undefined
    config: Config | undefined

    /**
     * Parses the list of assistants and returns the parsed resources and routes.
     * 
     * @param {Array} list - The list of assistants to parse.
     * @returns {Object} - An object containing the parsed resources and routes.
     */
    parse = async (config: Config, option = RenderOptions.default, translate: TFunction) => {
        this.#resources = []
        this.#routes = []
        this.translate = translate
        this.config = config

        switch (option) {
            case RenderOptions.spaces: {
                const [spaceResources, spaceRoutes] = await this.parseSpaces(
                    config.spaces,
                    config.assistants
                )

                this.#resources = spaceResources as Space[]
                this.#routes = spaceRoutes

                break
            }

            default:
                console.warn('unknown render option: ', option)
        }

        return { resources: this.#resources, routes: this.#routes }
    }

    parseSpaces = async (rawSpaces: SpaceConfig[], rawAssistants: AssistantConfig[]): Promise<ParseSpaceResult> => {
        const routes: RouteObject[] = []
        const resources: Space[] = []

        for (const space of rawSpaces) {
            const spaceResource: Space = {
                ...space,
                path: '',
                defaultAssistant: '',
                routes: [],
                assistants: []
            }

            const spacePath = space.id
            const assistantConfigs = rawAssistants.filter((rawAssistant) =>
                space.assistants.includes(rawAssistant.id)
            )

            if (assistantConfigs.length === 0)
                continue

            const [assistants, assistantsRoutes] = await this.parseAssistants(
                assistantConfigs,
                space.assistantPathProperty,
                routes
            )

            const spaceRoute = await this.parseRoute(
                space,
                RenderOptions.spaces,
                spacePath
            )

            if (space.defaultAssistant) {
                // check if default assistant is in the list
                let defaultAssistant = assistantsRoutes.find(
                    (assistant) => assistant?.id === space.defaultAssistant
                )

                // if not, check if the id changed
                if (!defaultAssistant) {
                    const oldDefaultAssistant = assistants.find(assistant => assistant.config.oldId === space.defaultAssistant)

                    defaultAssistant = assistantsRoutes.find(
                        (assistant) => assistant.id === oldDefaultAssistant?.config?.id
                    )

                    // if not, use the first assistant
                    if (!defaultAssistant) {
                        defaultAssistant = assistantsRoutes[0]
                        console.warn('default assistant is empty - using instead', defaultAssistant)
                    }
                }

                spaceResource.defaultAssistant = defaultAssistant.id
            }

            if (spaceRoute) {
                spaceRoute.children = assistantsRoutes
                routes.push(spaceRoute)
            }

            spaceResource.assistants = assistants
            spaceResource.routes = assistantsRoutes
            spaceResource.path = spacePath

            resources.push(spaceResource)
        }

        return [resources, routes]
    }

    parseAssistants = async (list: AssistantConfig[], pathProperty: string = 'id', existingRoutes: RouteObject[]): Promise<ParseAssistantResult> => {
        const translate = this.translate
        if (!translate) throw new Error('[SpaceParser] the translate function is not set: parse() sets it')

        const parsedAssistants: Assistant[] = []
        const assistantsRoutes: RouteObject[] = []
        for (const assistant of list) {
            type AssistantKey = typeof assistant
            type AiKey = typeof assistant.aiConfig

            type AssistantConfigKey = keyof AssistantKey
            type AiConfigKey = keyof AiKey

            const integratedId = utils.integrateId(assistant.id, existingRoutes)
            const assistantCopy = { ...assistant }

            const configuredPath = assistantCopy[pathProperty as AssistantConfigKey] || assistantCopy.aiConfig[pathProperty as AiConfigKey]
            const assistantPath = typeof configuredPath === 'string' ? configuredPath : ''

            assistantCopy.oldId = assistantCopy.id
            assistantCopy.id = integratedId
            assistantCopy.historyLoaded = false
            assistantCopy.aiConfig = { ...assistantCopy.aiConfig, endpoint: this.config?.endpoint || '' }

            const parsedAssistant = new Assistant(assistantCopy, assistantPath, translate)

            await parsedAssistant.init()

            const assistantRoute = await this.parseRoute(
                parsedAssistant,
                assistantCopy.type,
                assistantPath
            )

            if (assistantRoute) assistantsRoutes.push(assistantRoute)
            parsedAssistants.push(parsedAssistant)
        }

        return [parsedAssistants, assistantsRoutes]
    }

    // A basic assistant has no component of its own that sends the requests
    warnWithoutRequestHandler = () => {
        console.warn('[SpaceParser] a basic assistant has no handler for the requests of the chat')
    }

    /**
     * Parses a assistant and adds it to the routes.
     * @param {Object} assistant - The assistant to parse.
     */
    parseRoute = async (resource: SpaceConfig | Assistant, type: string, routeName: string): Promise<NonIndexRouteObject | undefined> => {
        let route: NonIndexRouteObject | undefined

        switch (type) {
            case AssistantTypes.BASIC: {
                const assistantConfig = resource as Assistant
                route = {
                    id: assistantConfig.config.id,
                    path: routeName,
                    element: <Chat key={assistantConfig.config.id} advancedSendRequest={this.warnWithoutRequestHandler} />,
                }

                break
            }

            case AssistantTypes.ADVANCED: {
                const assistantConfig = resource as Assistant
                const assistantComponentName = assistantConfig.config.component

                await import(
                    `@/ui/advancedAssistants/${assistantComponentName}/${assistantComponentName}.tsx`
                ).then(({ default: ChatComponent }) => {
                    route = {
                        id: assistantConfig.config.id,
                        path: routeName,
                        element: <ChatComponent key={assistantConfig.config.id} />,
                    }
                })

                break
            }

            case RenderOptions.spaces: {
                const spaceConfig = resource as SpaceConfig

                route = {
                    id: spaceConfig.id,
                    path: `/${routeName}`,
                }

                break
            }

            default:
                console.warn('unknown assistant type')
        }

        return route
    }
}

export default new SpaceParser()
export { AssistantTypes as AssistantsTypes }
